import React, { useState } from "react";
import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { IlingnunUkpiginHeaderInfo } from "./Sections/IlingnunUkpiginHeaderInfo";
import { Form } from "react-final-form";
import { IlingnunUkpiginFormResult } from "./ilingnunUkpigin";
import { PersonalInformation } from "./Sections/PersonalInformation";
import { ContactInformation } from "./Sections/ContactInformation";
import { HealthInformation } from "./Sections/HealthInformation";
import { IlingnunUkpiginConsent } from "./Sections/IlingnunUkpiginConsent";
import { useRecaptchaReady } from "../common/hooks/useRecaptchaReady";
import {
  getIlingnunUkpiginSettings,
  submitIlingnunUkpiginHealthDisclosure,
} from "../common/api/ilingnunUkpiginApi";
import { useNavigate } from "react-router-dom";
import { SubmitButton } from "../common/components/SubmitButton";
import { ErrorNotification } from "../common/components/ErrorNotification";
import { Helmet } from "react-helmet";
import { useAsyncData } from "../common/networking";

const RECAPTCHA_ACTION = "SubmitIlingnunUkpiginForm";

export interface IlingnunUkpiginFormProps {}

export function IlingnunUkpiginForm({}: IlingnunUkpiginFormProps) {
  const recaptchaReady = useRecaptchaReady();
  const navigate = useNavigate();

  const [showError, setShowError] = useState(false);

  const submit = async (submission: IlingnunUkpiginFormResult) => {
    try {
      const clientKey = process.env.REACT_APP_RECAPTCHA_CLIENT_KEY ?? "";
      const token = await grecaptcha.enterprise.execute(clientKey, {
        action: RECAPTCHA_ACTION,
      });

      await submitIlingnunUkpiginHealthDisclosure(
        submission,
        token,
        RECAPTCHA_ACTION
      );

      navigate("/success");
    } catch {
      setShowError(true);
    }
  };

  const { data: formSettings, isLoading } = useAsyncData(
    () => getIlingnunUkpiginSettings(),
    []
  );

  if (isLoading) {
    return (
      <Box>
        <Skeleton height={60} />
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </Box>
    );
  }

  if (!formSettings?.enabled) {
    return (
      <Typography fontSize={24} textAlign={"center"}>
        The Ilingnun Ukpigin form is currently not available.
      </Typography>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          ILINGNUN UKPIGIN: Personal and Health Information Disclosure Form
        </title>
      </Helmet>
      <Box>
        <IlingnunUkpiginHeaderInfo />
        <Form<IlingnunUkpiginFormResult>
          onSubmit={submit}
          subscription={{ values: true, invalid: true, submitting: true }}
          render={({ handleSubmit, invalid, submitting }) => (
            <>
              <Stack gap={3} divider={<Divider />}>
                <PersonalInformation />
                <ContactInformation />
                <HealthInformation />
                <IlingnunUkpiginConsent />
              </Stack>
              <Box ml={5} mt={3}>
                <SubmitButton
                  type="submit"
                  disabled={invalid || !recaptchaReady}
                  isSubmitting={submitting}
                  onClick={handleSubmit}
                />
              </Box>
            </>
          )}
        />

        <ErrorNotification
          open={showError}
          onClose={() => setShowError(false)}
        />
      </Box>
    </>
  );
}
